import React, { useEffect, useMemo, useState } from 'react';

export type CalendarView = 'week' | 'month';

export type CalendarEvent<T = any> = {
  id: string;
  title: string;
  subtitle?: string;
  kind?: 'WORK' | 'BREAK' | 'INFO';
  start: Date;
  end: Date;
  data?: T;
};

type Props<T> = {
  view: CalendarView;
  baseDate: Date;
  onViewChange: (v: CalendarView) => void;
  onBaseDateChange: (d: Date) => void;
  events: CalendarEvent<T>[];
  onSelectEvent?: (e: CalendarEvent<T>) => void;
  onSelectDate?: (d: Date) => void;
  title?: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const PX_PER_MIN = 1.05; // ~63px/h

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

function startOfWeekMonday(d: Date) {
  const base = startOfDay(d);
  const jsDay = base.getDay(); // 0=Sun
  const offset = (jsDay + 6) % 7; // 0=Mon
  return addDays(base, -offset);
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function ymd(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function minutesSinceStartOfDay(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}

function formatRangeTitle(view: CalendarView, base: Date) {
  const itMY = new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' });
  if (view === 'month') return itMY.format(base);

  const s = startOfWeekMonday(base);
  const e = addDays(s, 6);
  const fmt = new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' });
  return `${fmt.format(s)} – ${fmt.format(e)} · ${base.getFullYear()}`;
}

function buildMonthGrid(base: Date) {
  const first = startOfMonth(base);
  const last = endOfMonth(base);
  const start = startOfWeekMonday(first);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return { first, last, cells };
}

type LaidEvent<T> = CalendarEvent<T> & { colIndex: number; colCount: number };

function layoutOverlaps<T>(events: CalendarEvent<T>[]): LaidEvent<T>[] {
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  const out: LaidEvent<T>[] = [];
  let i = 0;

  while (i < sorted.length) {
    // Build one overlap cluster
    const cluster: CalendarEvent<T>[] = [];
    let clusterEnd = sorted[i].end.getTime();
    cluster.push(sorted[i]);
    i++;

    while (i < sorted.length && sorted[i].start.getTime() < clusterEnd) {
      cluster.push(sorted[i]);
      clusterEnd = Math.max(clusterEnd, sorted[i].end.getTime());
      i++;
    }

    // Assign columns inside cluster
    const active: { end: number; col: number }[] = [];
    const freeCols: number[] = [];
    let maxCols = 0;
    const laid: Map<string, { col: number }> = new Map();

    for (const ev of cluster.sort((a, b) => a.start.getTime() - b.start.getTime())) {
      const t = ev.start.getTime();

      // release finished
      for (let k = active.length - 1; k >= 0; k--) {
        if (active[k].end <= t) {
          freeCols.push(active[k].col);
          active.splice(k, 1);
        }
      }

      const col = freeCols.length ? freeCols.shift()! : maxCols;
      if (col === maxCols) maxCols += 1;

      active.push({ end: ev.end.getTime(), col });
      active.sort((a, b) => a.end - b.end);
      laid.set(ev.id, { col });
    }

    for (const ev of cluster) {
      const info = laid.get(ev.id)!;
      out.push({ ...ev, colIndex: info.col, colCount: maxCols });
    }
  }

  return out;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function fmtHM(d: Date) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function Calendar<T>(props: Props<T>) {
  const { view, baseDate, onViewChange, onBaseDateChange, events, onSelectEvent, onSelectDate, title } = props;

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 560);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 560);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const titleText = useMemo(() => formatRangeTitle(view, baseDate), [view, baseDate]);
  const now = new Date();

  const monthGrid = useMemo(() => buildMonthGrid(baseDate), [baseDate]);

  const visibleDays = useMemo(() => {
    const s = startOfWeekMonday(baseDate);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [baseDate]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent<T>[]>();
    for (const ev of events) {
      const dayKey = ymd(ev.start);
      const list = map.get(dayKey) || [];
      list.push(ev);
      map.set(dayKey, list);
    }
    return map;
  }, [events]);

  function goPrev() {
    if (view === 'week') onBaseDateChange(addDays(baseDate, -7));
    else onBaseDateChange(addMonths(baseDate, -1));
  }

  function goNext() {
    if (view === 'week') onBaseDateChange(addDays(baseDate, +7));
    else onBaseDateChange(addMonths(baseDate, +1));
  }

  function goToday() {
    onBaseDateChange(new Date());
  }

  return (
    <div className="cal">
      <div className="calToolbar">
        <div className="calToolbarLeft">
          {title ? <div className="h2" style={{ margin: 0 }}>{title}</div> : null}
          <div className="small">{titleText}</div>
        </div>

        <div className="calToolbarRight">
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="btn btnSecondary" onClick={goPrev} aria-label="Precedente">◀</button>
            <button className="btn btnSecondary" onClick={goToday}>Oggi</button>
            <button className="btn btnSecondary" onClick={goNext} aria-label="Successivo">▶</button>
          </div>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(['month', 'week'] as CalendarView[]).map(v => (
              <button
                key={v}
                className={`btn ${view === v ? 'btnPrimary' : 'btnSecondary'}`}
                style={{ minHeight: 40, padding: '10px 12px' }}
                onClick={() => onViewChange(v)}
              >
                {v === 'month' ? 'Mese' : 'Settimana'}
              </button>
            ))}
          </div>

          {isMobile && view === 'week' ? (
            <div className="small" style={{ textAlign:'right' }}>
              Scorri a lato per vedere gli altri giorni →
            </div>
          ) : null}
        </div>
      </div>

      {view === 'month' ? (
        <div className="calMonth">
          <div className="calMonthHeader">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(x => (
              <div key={x} className="small" style={{ textAlign: 'center' }}>{x}</div>
            ))}
          </div>

          <div className="calMonthGrid">
            {monthGrid.cells.map((d, idx) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === baseDate.getMonth();
              const list = (eventsByDay.get(key) || []).sort((a, b) => a.start.getTime() - b.start.getTime());
              const shown = list.slice(0, 3);
              const isToday = ymd(d) === ymd(now);

              return (
                <button
                  key={idx}
                  className="calMonthCell"
                  style={{ opacity: inMonth ? 1 : 0.45, outline: isToday ? '2px solid rgba(220,20,60,.55)' : 'none', outlineOffset: -2 }}
                  onClick={() => {
                    onSelectDate?.(d);
                    onBaseDateChange(d);
                    onViewChange('week');
                  }}
                >
                  <div className="calMonthCellTop">
                    <div className="calMonthDayNum">{d.getDate()}</div>
                    {list.length ? <span className="badge badgeGray">{list.length}</span> : null}
                  </div>

                  <div className="calMonthCellEvents">
                    {shown.map(ev => (
                      <div key={ev.id} className={`calMiniEvent ${ev.kind === 'BREAK' ? 'calMiniEventBreak' : 'calMiniEventWork'}`}>
                        <span style={{ fontWeight: 900 }}>{fmtHM(ev.start)}</span>
                        <span style={{ marginLeft: 6, opacity: 0.9 }}>{ev.title}</span>
                      </div>
                    ))}
                    {list.length > 3 ? <div className="small">+{list.length - 3}…</div> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {view === 'week' ? (
        <div className={`calTimeWrap ${isMobile ? 'calTimeWrapMobile' : ''}`}>
          <div className="calTimeHeader">
            <div className="calTimeAxisHeader" />
            <div className="calTimeDaysHeader">
              {visibleDays.map((d) => {
                const isToday = ymd(d) === ymd(now);
                return (
                  <div key={ymd(d)} className="calDayHeader" style={isToday ? { background: 'rgba(220,20,60,.10)' } : undefined}>
                    <div style={{ fontWeight: 900 }}>{new Intl.DateTimeFormat('it-IT', { weekday: 'short' }).format(d)}</div>
                    <div className="small">{new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' }).format(d)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="calTimeBody">
            <div className="calTimeAxis">
              {Array.from({ length: 25 }, (_, h) => (
                <div key={h} className="calHourLabel" style={{ height: h === 24 ? 0 : 60 * PX_PER_MIN }}>
                  {h < 24 ? `${pad2(h)}:00` : ''}
                </div>
              ))}
            </div>

            <div className="calTimeDays" style={{ height: 24 * 60 * PX_PER_MIN }}>
              {visibleDays.map((day) => {
                const key = ymd(day);
                const dayEvents = (eventsByDay.get(key) || []).map(ev => {
                  // clamp to day
                  const start = new Date(Math.max(ev.start.getTime(), day.getTime()));
                  const end = new Date(Math.min(ev.end.getTime(), day.getTime() + DAY_MS));
                  return { ...ev, start, end };
                }).filter(ev => ev.end.getTime() > ev.start.getTime());

                const laid = layoutOverlaps(dayEvents);

                const isToday = ymd(day) === ymd(now);
                const nowTop = clamp(minutesSinceStartOfDay(now), 0, 24 * 60) * PX_PER_MIN;

                return (
                  <div key={key} className="calDayCol">
                    {/* hour lines */}
                    {Array.from({ length: 24 }, (_, h) => (
                      <div key={h} className="calHourLine" style={{ top: h * 60 * PX_PER_MIN }} />
                    ))}

                    {isToday ? <div className="calNowLine" style={{ top: nowTop }} /> : null}

                    {laid.map((ev) => {
                      const top = minutesSinceStartOfDay(ev.start) * PX_PER_MIN;
                      const endMin = minutesSinceStartOfDay(ev.end);
                      const height = Math.max(18, (endMin - minutesSinceStartOfDay(ev.start)) * PX_PER_MIN);
                      const widthPct = 100 / ev.colCount;
                      const leftPct = ev.colIndex * widthPct;

                      return (
                        <button
                          key={ev.id}
                          className={`calEvent ${ev.kind === 'BREAK' ? 'calEventBreak' : 'calEventWork'}`}
                          style={{ top, height, left: `${leftPct}%`, width: `${widthPct}%` }}
                          onClick={() => onSelectEvent?.(ev)}
                          title={`${fmtHM(ev.start)}–${fmtHM(ev.end)} ${ev.title}`}
                        >
                          <div className="calEventTitle" style={{ marginBottom: 4 }}>
                            <span style={{ fontWeight: 900 }}>{fmtHM(ev.start)}–{fmtHM(ev.end)}</span>
                          </div>
                          <div className="calEventBody">
                            <div style={{ fontWeight: 900 }}>{ev.title}</div>
                            {ev.subtitle ? <div className="small" style={{ color: 'rgba(255,255,255,.75)' }}>{ev.subtitle}</div> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {view === 'week' ? (
        <div className="small" style={{ marginTop: 10 }}>
          Suggerimento: clicca un blocco per vedere i dettagli.
        </div>
      ) : null}
    </div>
  );
}
